<template>
  <div class="compare-html">
    <!-- 左侧选择区域，复用 UpdateHtml 的选择逻辑 -->
    <div class="left-panel">
      <!-- 添加搜索框 -->
      <div class="search-box">
        <div class="search-input-wrapper">
          <input 
            type="text" 
            v-model="searchQuery" 
            placeholder="doi/paper_id/id"
            @keyup.enter="handleSearch"
            @input="handleSearchInput"
          />
          <div 
            class="search-icon" 
            @click="clearSearch"
            :title="isSearching ? '搜索中...' : '清除搜索'"
          >
            <span v-if="isSearching" class="loading-dot">...</span>
            <span v-else-if="searchQuery" class="clear-icon">×</span>
            <span v-else class="search-icon">🔍</span>
          </div>
        </div>
        <button 
          class="search-btn" 
          @click="handleSearch"
          :disabled="!searchQuery.trim()"
        >
          搜索
        </button>
      </div>

      <!-- 搜索结果或原有的选择器 -->
      <div class="selectors" v-if="!hasSearched">
        <!-- 原有的期刊、卷、文章选择器保持不变 -->
        <!-- 期刊选择 -->
        <div class="selector-item">
          <label>期刊/论文集:</label>
          <div class="select-container" ref="journalSelect">
            <div class="select-options" @scroll="handleJournalScroll" ref="journalOptions">
              <div 
                v-for="journal in journals" 
                :key="journal.id"
                class="select-option"
                :class="{ active: selectedJournal && selectedJournal.id === journal.id }"
                @click="selectJournal(journal)"
              >
                {{ journal.abbreviation || journal.name || '未命名期刊' }}
              </div>
              <div v-if="isLoadingJournals" class="loading">加载中...</div>
            </div>
          </div>
        </div>

        <!-- 卷选择 -->
        <div class="selector-item" v-if="selectedJournal">
          <label>卷:</label>
          <div class="select-container" ref="volumeSelect">
            <div 
              class="select-options" 
              @scroll="handleVolumeScroll"
              ref="volumeOptions"
            >
              <div 
                v-for="volume in volumes" 
                :key="volume.id"
                class="select-option"
                :class="{ active: selectedVolume && selectedVolume.id === volume.id }"
                @click="selectVolume(volume)"
              >
                {{ volume.volume }}
              </div>
              <div v-if="isLoadingVolumes" class="loading">加载中...</div>
            </div>
          </div>
        </div>

        <!-- 文章选择 -->
        <div class="selector-item" v-if="selectedVolume">
          <label>文章:</label>
          <div class="select-container" ref="articleSelect">
            <div 
              class="select-options" 
              @scroll="handleArticleScroll"
              ref="articleOptions"
            >
              <div 
                v-for="article in articles" 
                :key="article.id"
                class="select-option"
                :class="{
                  active: selectedArticle && selectedArticle.id === article.id,
                  confirmed: article.is_confirm_body
                }"
                @click="selectArticle(article)"
              >
                <div class="article-info">
                  <span class="article-id">{{ article.doi_suffix || '无编号' }}</span>
                  <span 
                    class="confirm-status" 
                    :class="{ confirmed: article.is_confirm_body }"
                    :title="article.is_confirm_body ? '已确认' : '未确认'"
                  >
                    {{ article.is_confirm_body ? '✓' : '?' }}
                  </span>
                </div>
              </div>
              <div v-if="isLoadingArticles" class="loading">加载中...</div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="search-results">
        <div 
          v-for="article in searchResults" 
          :key="article.id"
          class="search-result-item"
          :class="{
            active: selectedArticle && selectedArticle.id === article.id,
            confirmed: article.is_confirm_body
          }"
          @click="selectSearchResult(article)"
        >
          <div class="article-info">
            <div class="article-meta">
              <span class="article-id">{{ article.doi_suffix || '无编号' }}</span>
              <span class="journal-info" v-if="article.journal_name">
                {{ article.journal_name }} - Vol.{{ article.volume_number }}
              </span>
            </div>
            <span 
              class="confirm-status" 
              :class="{ confirmed: article.is_confirm_body }"
              :title="article.is_confirm_body ? '已确认' : '未确认'"
            >
              {{ article.is_confirm_body ? '✓' : '?' }}
            </span>
          </div>
        </div>
        <div v-if="isSearching" class="loading">搜索中...</div>
        <div v-else-if="searchResults.length === 0" class="no-results">
          未找到匹配的文章
        </div>
      </div>
    </div>

    <!-- 右侧对比区域 -->
    <div class="right-panel">
      <div class="compare-container" v-if="selectedArticle">
        <!-- 工具栏 -->
        <div class="toolbar">
          <button 
            class="edit-btn" 
            @click="openEditor" 
            title="编辑文章内容"
          >
            编辑内容
          </button>
          <button 
            class="preview-btn" 
            v-if="selectedArticle.web_url"
            @click="openWebUrl"
            title="在新窗口中打开"
          >
            打开页面
          </button>
          <button 
            class="refresh-btn" 
            @click="refreshWebContent"
            :disabled="isRefreshing"
            title="刷新预览内容"
          >
            {{ isRefreshing ? '刷新中...' : '刷新' }}
          </button>
          <button 
            class="archive-btn" 
            @click="confirmArticle"
            :disabled="isConfirming"
            title="确认并存档文章"
          >
            {{ isConfirming ? '存档中...' : '存档' }}
          </button>
          <span class="article-id-display" v-if="selectedArticle">
            ID: {{ selectedArticle.id }}
          </span>
        </div>

        <!-- 对比区域 -->
        <div class="compare-content">
          <!-- PDF 预览 -->
          <div class="pdf-preview">
            <h3>PDF 版本</h3>
            <div class="iframe-container">
              <iframe 
                v-if="selectedArticle.pdf_url" 
                :src="selectedArticle.pdf_url" 
                frameborder="0"
              ></iframe>
              <div v-else class="no-content">
                暂无 PDF 内容
              </div>
            </div>
          </div>

          <!-- Web 预览 -->
          <div class="web-preview">
            <h3>Web 版本</h3>
            <div class="iframe-container">
              <iframe 
                v-if="selectedArticle.web_url" 
                :src="selectedArticle.web_url" 
                frameborder="0"
              ></iframe>
              <div v-else class="no-content">
                暂无 Web 内容
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="no-selection">
        请选择要对比的文章
      </div>
    </div>

    <!-- 添加确认对话框 -->
    <div v-if="showConfirmDialog" class="confirm-dialog-overlay">
      <div class="confirm-dialog">
        <h4>确认存档</h4>
        <p>确定要存档这篇文章吗？存档后将标记为已确认状态。</p>
        <div class="confirm-buttons">
          <button 
            class="confirm-btn"
            @click="handleConfirm"
            :disabled="isConfirming"
          >
            {{ isConfirming ? '存档中...' : '确认' }}
          </button>
          <button 
            class="cancel-btn"
            @click="showConfirmDialog = false"
            :disabled="isConfirming"
          >
            取消
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import apiService from '../api'

// 添加搜索相关的状态
const searchQuery = ref('')
const searchResults = ref([])
const isSearching = ref(false)

// 添加搜索状态标记
const hasSearched = ref(false)

// 添加存档状态
const isConfirming = ref(false)

// 添加确认对话框状态
const showConfirmDialog = ref(false)

// 添加刷新状态
const isRefreshing = ref(false)

// 状态定义
const journals = ref([])
const volumes = ref([])
const articles = ref([])
const selectedJournal = ref('')
const selectedVolume = ref('')
const selectedArticle = ref({
  id: '',
  web_url: ''
})

// 加载状态
const isLoadingJournals = ref(false)
const isLoadingVolumes = ref(false)
const isLoadingArticles = ref(false)

// 分页相关
const journalPage = ref(1)
const volumePage = ref(1)
const articlePage = ref(1)
const hasMoreJournals = ref(true)
const hasMoreVolumes = ref(true)
const hasMoreArticles = ref(true)

// 获取期刊列表
const fetchJournals = async (page = 1, append = false) => {
  if (!hasMoreJournals.value || isLoadingJournals.value) return
  
  isLoadingJournals.value = true
  try {
    const data = await apiService.getJournals(page, 20)
    if (data.success) {
      if (Array.isArray(data.data.list)) {
        if (append) {
          journals.value = [...journals.value, ...data.data.list]
        } else {
          journals.value = data.data.list
        }
        hasMoreJournals.value = page < data.data.total_pages
        journalPage.value = page
      }
    }
  } catch (error) {
    console.error('获取期刊列表失败:', error)
  } finally {
    isLoadingJournals.value = false
  }
}

// 获取卷列表
const fetchVolumes = async (journalId, page = 1, append = false) => {
  if (!hasMoreVolumes.value || isLoadingVolumes.value) return

  isLoadingVolumes.value = true
  try {
    const data = await apiService.getVolumes(journalId, page, 20)
    if (data.success) {
      if (append) {
        volumes.value = [...volumes.value, ...data.data.list]
      } else {
        volumes.value = data.data.list
      }
      hasMoreVolumes.value = page < data.data.total_pages
      volumePage.value = page
    }
  } catch (error) {
    console.error('获取卷列表失败:', error)
  } finally {
    isLoadingVolumes.value = false
  }
}

// 获取文章列表
const fetchArticles = async (volumeId, page = 1, append = false) => {
  if (!hasMoreArticles.value || isLoadingArticles.value) return

  isLoadingArticles.value = true
  try {
    const data = await apiService.getArticles(volumeId, page, 20)
    if (data.success) {
      if (append) {
        articles.value = [...articles.value, ...data.data.list]
      } else {
        articles.value = data.data.list
      }
      hasMoreArticles.value = page < data.data.total_pages
      articlePage.value = page
    }
  } catch (error) {
    console.error('获取文章列表失败:', error)
  } finally {
    isLoadingArticles.value = false
  }
}

// 选择处理函数
const selectJournal = async (journal) => {
  selectedJournal.value = journal
  selectedVolume.value = null
  selectedArticle.value = { id: '', pdf_url: '', web_url: '', body_html: '' }
  volumes.value = []
  articles.value = []
  volumePage.value = 1
  hasMoreVolumes.value = true
  await fetchVolumes(journal.id, 1)
}

const selectVolume = async (volume) => {
  selectedVolume.value = volume
  selectedArticle.value = { id: '', pdf_url: '', web_url: '', body_html: '' }
  articles.value = []
  articlePage.value = 1
  hasMoreArticles.value = true
  await fetchArticles(volume.id, 1)
}

// 添加获取文章内容的函数
const fetchArticleContent = async (articleId) => {
  try {
    const data = await apiService.getArticleBody(articleId)
    if (data.success) {
      selectedArticle.value = {
        ...selectedArticle.value,
        pdf_url: data.data.pdf_url,
        web_url: data.data.web_url
      }
    }
  } catch (error) {
    console.error('获取文章内容失败:', error)
  }
}

// 修改选择文章的函数
const selectArticle = (article) => {
  selectedArticle.value = article
  fetchArticleContent(article.id)
}

// 滚动处理函数
const handleJournalScroll = (e) => {
  const { scrollTop, scrollHeight, clientHeight } = e.target
  if (scrollHeight - scrollTop - clientHeight < 50) {
    fetchJournals(journalPage.value + 1, true)
  }
}

const handleVolumeScroll = (e) => {
  const { scrollTop, scrollHeight, clientHeight } = e.target
  if (scrollHeight - scrollTop - clientHeight < 50 && selectedJournal.value) {
    fetchVolumes(selectedJournal.value.id, volumePage.value + 1, true)
  }
}

const handleArticleScroll = (e) => {
  const { scrollTop, scrollHeight, clientHeight } = e.target
  if (scrollHeight - scrollTop - clientHeight < 50 && selectedVolume.value) {
    fetchArticles(selectedVolume.value.id, articlePage.value + 1, true)
  }
}

// 修改搜索处理函数
const handleSearch = async () => {
  if (!searchQuery.value.trim()) return
  
  isSearching.value = true
  hasSearched.value = true // 标记已执行搜索
  try {
    const data = await apiService.searchArticles(
      searchQuery.value.trim(),
      selectedVolume.value?.id,
      1,
      20
    )
    
    if (data.success) {
      searchResults.value = data.data.list
    }
  } catch (error) {
    console.error('搜索失败:', error)
  } finally {
    isSearching.value = false
  }
}

// 选择搜索结果
const selectSearchResult = async (article) => {
  selectedArticle.value = { ...article }
  await fetchArticleContent(article.id)
}

// 修改清除搜索函数
const clearSearch = () => {
  searchQuery.value = ''
  searchResults.value = []
  hasSearched.value = false // 重置搜索状态
}

// 添加打开网页函数
const openWebUrl = () => {
  if (selectedArticle.value?.web_url) {
    const newUrl = selectedArticle.value.web_url.replace(
      "read.ewapublishing.org",
      "direct.ewa.pub"
    )
    window.open(newUrl, '_blank')
  }
}

// 添加输入处理函数
const handleSearchInput = () => {
  if (!searchQuery.value.trim()) {
    // 当搜索框为空时，重置搜索状态
    searchResults.value = []
    hasSearched.value = false
  }
}

// 修改存档函数
const confirmArticle = () => {
  showConfirmDialog.value = true
}

// 添加确认处理函数
const handleConfirm = async () => {
  if (!selectedArticle.value?.id || isConfirming.value) return
  
  isConfirming.value = true
  try {
    const data = await apiService.confirmArticle(selectedArticle.value.id)
    
    if (data.success) {
      selectedArticle.value.is_confirm_body = true
      const searchArticle = searchResults.value.find(a => a.id === selectedArticle.value.id)
      if (searchArticle) {
        searchArticle.is_confirm_body = true
      }
      const listArticle = articles.value.find(a => a.id === selectedArticle.value.id)
      if (listArticle) {
        listArticle.is_confirm_body = true
      }
    }
  } catch (error) {
    console.error('存档失败:', error)
  } finally {
    isConfirming.value = false
    showConfirmDialog.value = false
  }
}

// 修改打开编辑器函数
const openEditor = () => {
  if (selectedArticle.value?.id) {
    window.open(`/edit?article_id=${selectedArticle.value.id}`, '_blank')
  }
}

// 添加刷新函数
const refreshWebContent = async () => {
  if (!selectedArticle.value?.id || isRefreshing.value) return
  
  isRefreshing.value = true
  try {
    const data = await apiService.getArticleBody(selectedArticle.value.id)
    
    if (data.success) {
      // 更新文章的 web_url
      selectedArticle.value = {
        ...selectedArticle.value,
        web_url: data.data.web_url
      }
      
      // 如果 iframe 存在，重新加载
      const webFrame = document.querySelector('.web-preview iframe')
      if (webFrame) {
        webFrame.src = data.data.web_url
      }
    }
  } catch (error) {
    console.error('刷新内容失败:', error)
  } finally {
    isRefreshing.value = false
  }
}

// 初始化
onMounted(() => {
  fetchJournals()
})
</script>

<style scoped>
.compare-html {
  display: flex;
  gap: 20px;
  padding: 20px;
  height: calc(100vh - 100px); /* 减去导航栏和padding的高度 */
}

.left-panel {
  width: 220px;
  padding: 10px;
  background: #f5f5f5;
  border-radius: 8px;
}

.right-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.compare-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.toolbar {
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 10px;
  background: #f5f5f5;
  border-radius: 4px;
}

.compare-content {
  flex: 1;
  display: flex;
  gap: 20px;
  overflow: hidden;
}

.pdf-preview,
.web-preview {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #fff;
  border-radius: 4px;
  overflow: hidden;
}

h3 {
  margin: 0;
  padding: 10px;
  background: #f5f5f5;
  border-bottom: 1px solid #ddd;
}

iframe {
  flex: 1;
  width: 100%;
  border: none;
}

.no-content {
  padding: 20px;
  text-align: center;
  color: #999;
}

.no-selection {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
  font-size: 16px;
}

.edit-btn {
  padding: 8px 16px;
  background-color: #4a90e2;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.edit-btn:hover {
  background-color: #357abd;
}

.selector-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.selector-item label {
  font-size: 13px;
  font-weight: bold;
  color: #333;
}

.select-container {
  position: relative;
  height: 180px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
}

.select-options {
  height: 100%;
  overflow-y: auto;
  padding: 0 0;
}

.select-option {
  padding: 6px 10px;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  font-size: 13px;
}

.select-option:hover {
  background-color: #f0f0f0;
}

.select-option.active {
  background-color: #e6f7ff;
  color: #1890ff;
}

.select-option.confirmed {
  border-left: 3px solid #52c41a;
}

.article-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.article-id {
  flex: 1;
}

.confirm-status {
  margin-left: 8px;
  font-size: 12px;
  padding: 2px 6px;
  border-radius: 10px;
  background-color: #f0f0f0;
  color: #999;
}

.confirm-status.confirmed {
  background-color: #e6f7ff;
  color: #1890ff;
}

.editor-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow: hidden;
}

.editor-toolbar {
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 10px;
  background: #f5f5f5;
  border-radius: 4px;
}

.back-btn {
  padding: 8px 16px;
  background-color: #666;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.back-btn:hover {
  background-color: #555;
}

.search-box {
  position: relative;
  margin-bottom: 10px;
  width: 180px;
  margin-left: auto;
  margin-right: auto;
  display: flex;
  gap: 4px;
  align-items: center;
}

.search-input-wrapper {
  position: relative;
  width: 120px;
  flex: none;
}

.search-box input {
  width: 100%;
  padding: 3px 20px 3px 6px;
  border: 1px solid #ddd;
  border-radius: 3px;
  font-size: 12px;
  outline: none;
  transition: border-color 0.3s;
  height: 24px;
  box-sizing: border-box;
}

.search-btn {
  height: 24px;
  padding: 0 8px;
  font-size: 12px;
  background-color: #4a90e2;
  color: white;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  flex: none;
}

.search-btn:hover {
  background-color: #357abd;
}

.search-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.search-icon {
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  padding: 2px 4px;
}

.loading-dot {
  animation: loading 1s infinite;
}

@keyframes loading {
  0%, 100% { opacity: 0.3; }
  50% { opacity: 1; }
}

.search-results {
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  max-height: calc(100vh - 140px);
  overflow-y: auto;
}

.search-result-item {
  padding: 12px;
  cursor: pointer;
  border-bottom: 1px solid #eee;
  transition: background-color 0.2s;
}

.search-result-item:last-child {
  border-bottom: none;
}

.search-result-item:hover {
  background-color: #f5f5f5;
}

.search-result-item.active {
  background-color: #e6f7ff;
}

.article-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.journal-info {
  font-size: 12px;
  color: #666;
}

.no-results {
  padding: 20px;
  text-align: center;
  color: #999;
}

.clear-icon {
  font-size: 14px;
  font-weight: bold;
}

.search-icon:hover {
  color: #666;
}

.preview-btn {
  padding: 8px 16px;
  background-color: #28a745; /* 使用绿色区分 */
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.preview-btn:hover {
  background-color: #218838;
}

.preview-btn:disabled {
  background-color: #a3d7b0;
  cursor: not-allowed;
}

.archive-btn {
  padding: 8px 16px;
  background-color: #ffa500; /* 使用橙色区分 */
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.archive-btn:hover {
  background-color: #ff8c00;
}

.archive-btn:disabled {
  background-color: #ffd699;
  cursor: not-allowed;
}

.iframe-container {
  flex: 1;
  position: relative;
  overflow: hidden;
}

iframe {
  width: 100%;
  height: 100%;
  border: none;
}

.confirm-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.confirm-dialog {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  width: 300px;
}

.confirm-dialog h4 {
  margin: 0 0 16px;
  font-size: 16px;
  color: #333;
}

.confirm-dialog p {
  margin: 0 0 20px;
  font-size: 14px;
  color: #666;
  line-height: 1.5;
}

.confirm-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.confirm-btn {
  padding: 6px 16px;
  background-color: #ffa500;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.confirm-btn:hover {
  background-color: #ff8c00;
}

.confirm-btn:disabled {
  background-color: #ffd699;
  cursor: not-allowed;
}

.cancel-btn {
  padding: 6px 16px;
  background-color: #f5f5f5;
  color: #666;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.cancel-btn:hover {
  background-color: #e8e8e8;
}

.cancel-btn:disabled {
  background-color: #f5f5f5;
  color: #999;
  cursor: not-allowed;
}

.save-btn {
  padding: 8px 16px;
  background-color: #52c41a; /* 使用绿色 */
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-left: auto; /* 将按钮推到右侧 */
}

.save-btn:hover {
  background-color: #389e0d;
}

.save-btn:disabled {
  background-color: #b7eb8f;
  cursor: not-allowed;
}

.save-status {
  margin-left: 10px;
  font-size: 14px;
}

.save-status.success {
  color: #52c41a;
}

.save-status.error {
  color: #f5222d;
}

.article-id-display {
  padding: 6px 12px;
  background-color: #f5f5f5;
  border-radius: 4px;
  color: #666;
  font-size: 14px;
  margin: 0 10px;
  border: 1px solid #ddd;
}

.refresh-btn {
  padding: 8px 16px;
  background-color: #1890ff; /* 使用蓝色 */
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.refresh-btn:hover {
  background-color: #096dd9;
}

.refresh-btn:disabled {
  background-color: #91d5ff;
  cursor: not-allowed;
}

/* ... 其他必要的样式 ... */
</style> 